import { useMemo, useState } from 'react'
import { Plus, Users } from 'lucide-react'
import { ChatSearchInput } from '@/components/chat/ChatSearchInput'
import { formatTime } from '@/utils/chatHelpers'
import { DmAvatarStack } from './DmAvatarStack'
import type { DmConversation } from '@/types/direct-message'

type ListFilter = 'all' | 'unread' | 'archived' | 'teacher'

const FILTER_CHIPS: { value: ListFilter; label: string }[] = [
  { value: 'all', label: 'Alle' },
  { value: 'unread', label: 'Ungelesen' },
  { value: 'archived', label: 'Archiv' },
  { value: 'teacher', label: 'Lehrpersonen' },
]

interface DmListPanelProps {
  conversations: DmConversation[]
  selectedId: string | null
  onSelect: (id: string) => void
  onNew: () => void
  isLoading: boolean
  isError?: boolean
  filter: ListFilter
  onFilterChange: (f: ListFilter) => void
  currentUserId?: string
  callerRole?: string
}

export function DmListPanel({
  conversations, selectedId, onSelect, onNew, isLoading, isError, filter, onFilterChange, currentUserId, callerRole,
}: DmListPanelProps) {
  const [q, setQ] = useState('')
  const chips = callerRole === 'teacher' ? FILTER_CHIPS.filter(c => c.value !== 'teacher') : FILTER_CHIPS
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase()
    return conversations.filter(c => {
      if (filter === 'archived' ? !c.is_archived : c.is_archived) return false
      if (filter === 'unread' && c.unread_count <= 0) return false
      if (filter === 'teacher' && !c.participants.some(p => p.role === 'teacher' && p.user_id !== currentUserId)) return false
      if (!term) return true
      return (
        c.display_title.toLowerCase().includes(term)
        || c.participants.some(p => p.name.toLowerCase().includes(term))
        || (c.last_message?.preview || '').toLowerCase().includes(term)
      )
    })
  }, [conversations, q, filter, currentUserId])

  return (
    <div className="w-full md:w-80 border-r border-gray-200 bg-white flex flex-col min-h-0">
      <div className="p-3 flex items-center justify-between border-b border-gray-200">
        <div className="flex gap-1 flex-wrap">
          {chips.map(f => (
            <button
              key={f.value}
              type="button"
              onClick={() => onFilterChange(f.value)}
              className={`px-2 py-1 text-xs rounded-full ${filter === f.value ? 'bg-slate-900 text-white' : 'bg-gray-100 text-gray-600'}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button type="button" onClick={onNew} className="p-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700" title="Neue Nachricht">
          <Plus className="w-4 h-4" />
        </button>
      </div>
      <ChatSearchInput value={q} onChange={setQ} placeholder="Nachrichten suchen…" ariaLabel="Nachrichten suchen" />
      <div className="flex-1 overflow-y-auto">
        {isLoading && <p className="p-4 text-sm text-gray-400">Laden…</p>}
        {isError && !isLoading && (
          <p className="p-4 text-sm text-red-500">Nachrichten konnten nicht geladen werden.</p>
        )}
        {!isLoading && !isError && filtered.length === 0 && (
          <p className="p-4 text-sm text-gray-400">Keine Konversationen.</p>
        )}
        {filtered.map(c => {
          const active = c.conversation_id === selectedId
          return (
            <button
              key={c.conversation_id}
              type="button"
              onClick={() => onSelect(c.conversation_id)}
              className={`w-full flex items-center gap-3 px-3 py-3 text-left border-b border-gray-100 ${active ? 'chat-item-active' : 'hover:bg-gray-50'}`}
            >
              <DmAvatarStack participants={c.participants} isActive={active} currentUserId={currentUserId} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  {c.is_group && <Users className="w-3 h-3 text-gray-400 shrink-0" />}
                  <span className="text-sm font-medium truncate text-gray-900">{c.display_title}</span>
                  {c.last_message && (
                    <span className="ml-auto text-[10px] text-gray-400 shrink-0">{formatTime(c.last_message.created_at)}</span>
                  )}
                </div>
                <div className="text-xs text-gray-500 truncate">{c.last_message?.preview || 'Keine Nachrichten'}</div>
              </div>
              {c.unread_count > 0 && (
                <span className="text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-blue-500">
                  {c.unread_count > 99 ? '99+' : c.unread_count}
                </span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
